import React, { useState, useEffect } from 'react';

const KeyFeatures = () => {
  const [animatedTitle, setAnimatedTitle] = useState('');
  const [currentFeature, setCurrentFeature] = useState(0);
  
  const fullTitle = "Ultimate Social Features for Connection & Celebration";
  
  const features = [
    {
      icon: '👥',
      title: 'Create Custom Groups',
      description: 'Build public or private communities with custom fields and categories',
      gradient: 'from-pink-500 to-purple-500',
      stats: '50K+ Active Groups'
    },
    {
      icon: '📹',
      title: 'Group Video Calls',
      description: 'HD video calls with up to 100 participants, screen sharing & filters',
      gradient: 'from-cyan-500 to-blue-500',
      stats: '10K+ Daily Calls'
    },
    {
      icon: '🎨',
      title: 'Custom Profile Fields',
      description: 'Design unique profiles with personalized fields and categories',
      gradient: 'from-yellow-500 to-orange-500',
      stats: 'Unlimited Customization'
    },
    {
      icon: '💬',
      title: 'Real-time Chat',
      description: 'Group messaging with file sharing, reactions, polls, and voice notes',
      gradient: 'from-green-500 to-teal-500',
      stats: '1M+ Messages/Day'
    },
    {
      icon: '🎵',
      title: 'Voice Rooms',
      description: 'Audio hangouts with music sharing, voice effects, and rooms',
      gradient: 'from-purple-500 to-pink-500',
      stats: '24/7 Voice Chat'
    },
    {
      icon: '🎭',
      title: 'Virtual Events',
      description: 'Host and join virtual events, parties, and live sessions',
      gradient: 'from-orange-500 to-red-500',
      stats: '500+ Events Monthly'
    }
  ];

  // Social platform floating elements
  const socialIcons = [
    { icon: '🎥', size: 'w-12 h-12', position: 'top-10 left-10', delay: 'delay-0' },
    { icon: '📞', size: 'w-10 h-10', position: 'top-20 right-20', delay: 'delay-1000' },
    { icon: '👥', size: 'w-11 h-11', position: 'bottom-20 left-20', delay: 'delay-2000' },
    { icon: '🎭', size: 'w-9 h-9', position: 'bottom-10 right-10', delay: 'delay-1500' },
    { icon: '🎵', size: 'w-10 h-10', position: 'top-1/3 left-1/4', delay: 'delay-500' },
    { icon: '💬', size: 'w-8 h-8', position: 'bottom-1/3 right-1/4', delay: 'delay-2500' },
    { icon: '✨', size: 'w-11 h-11', position: 'top-1/4 right-1/3', delay: 'delay-3000' },
    { icon: '🎊', size: 'w-9 h-9', position: 'bottom-1/4 left-1/3', delay: 'delay-3500' }
  ];

  useEffect(() => {
    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex <= fullTitle.length) {
        setAnimatedTitle(fullTitle.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(interval);
      }
    }, 40);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const featureInterval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % features.length);
    }, 4000);

    return () => clearInterval(featureInterval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-fuchsia-700 via-pink-600 to-orange-500 py-20 overflow-hidden relative">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -left-20 w-60 h-60 bg-yellow-500/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-20 -right-20 w-60 h-60 bg-pink-500/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/3 left-1/4 w-40 h-40 bg-orange-500/20 rounded-full blur-2xl animate-pulse delay-500"></div>
        
        {/* Floating particles */}
        {[...Array(15)].map((_, i) => (
          <div 
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.3}s`,
              opacity: 0.6
            }}
          />
        ))}
      </div>

      {/* Floating Social Icons */}
      {socialIcons.map((icon, index) => (
        <div
          key={index}
          className={`absolute ${icon.position} ${icon.size} bg-white/20 backdrop-blur-lg rounded-2xl border-2 border-white/30 flex items-center justify-center text-2xl animate-float-slow ${icon.delay} z-20 shadow-2xl hover:scale-125 transition-transform duration-300 cursor-pointer`}
          style={{
            animationDuration: `${7 + index}s`
          }}
        >
          {icon.icon}
        </div>
      ))}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Animated Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-md rounded-full px-6 py-3 border-2 border-white/30 mb-8 shadow-lg">
            <span className="w-3 h-3 bg-yellow-400 rounded-full animate-ping shadow-md"></span>
            <span className="text-white font-bold">MASTIVIBES - Your Ultimate Social Playground</span>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6">
            <span className="bg-gradient-to-r from-yellow-300 via-pink-400 to-orange-400 bg-clip-text text-transparent animate-gradient-flow">
              {animatedTitle}
            </span>
            <span className="animate-pulse text-yellow-300">|</span>
          </h2>
          
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed font-medium">
            Experience the future of social connection with features designed for creators, communities, 
            and everyone who loves to connect and celebrate together.
          </p>
        </div>

        {/* Main Features Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group relative"
            >
              <div className="bg-white/15 backdrop-blur-2xl rounded-3xl p-8 border-2 border-white/25 shadow-2xl transform transition-all duration-500 hover:scale-105 hover:rotate-2 h-full hover:shadow-pink-500/25">
                {/* Animated Icon Container */}
                <div className="relative mb-6">
                  <div className={`w-20 h-20 bg-gradient-to-r ${feature.gradient} rounded-2xl flex items-center justify-center text-3xl text-white shadow-2xl transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 ${index === currentFeature ? 'animate-pulse' : ''}`}>
                    {feature.icon}
                  </div>
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full animate-ping shadow-lg"></div>
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center shadow-md">
                    <span className="text-xs text-white font-bold">⚡</span>
                  </div>
                </div>

                {/* Feature Content */}
                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-yellow-300 transition-colors duration-300">
                  {feature.title}
                </h3>
                
                <p className="text-white/80 mb-6 leading-relaxed font-medium">
                  {feature.description}
                </p>

                {/* Stats Badge */}
                <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border-2 border-white/20 group-hover:border-yellow-400/50 transition-colors duration-300">
                  <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></span>
                  <span className="text-yellow-300 font-bold text-sm">{feature.stats}</span>
                </div>
              </div>

              {/* Hover Glow Effect */}
              <div className={`absolute inset-0 bg-gradient-to-r ${feature.gradient} rounded-3xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500 -z-10`}></div>
            </div>
          ))}
        </div>

        {/* Interactive Demo Section */}
        <div className="bg-white/15 backdrop-blur-2xl rounded-3xl p-8 border-2 border-white/25 shadow-2xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Left Side - Demo Content */}
            <div className="space-y-6">
              <h3 className="text-3xl font-bold text-white">
                Experience{' '}
                <span className="bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">
                  Social Connection
                </span>
                {' '}Like Never Before
              </h3>
              
              <p className="text-white/90 text-lg leading-relaxed font-medium">
                Join thousands of users creating meaningful connections through our powerful social features.
              </p>

              {/* Feature Showcase */}
              <div className="space-y-4">
                {[
                  'Create unlimited custom groups',
                  'Host group video calls with 100+ people',
                  'Design unique profile fields',
                  '24/7 voice chat rooms'
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3 group transform hover:translate-x-2 transition-transform duration-300">
                    <div className="w-8 h-8 bg-gradient-to-r from-yellow-400 to-pink-400 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <span className="text-white font-bold">✦</span>
                    </div>
                    <span className="text-white font-semibold group-hover:text-yellow-300 transition-colors duration-300">
                      {item}
                    </span>
                  </div>
                ))}
              </div>

              <button className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 to-pink-500 rounded-xl blur opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>
                <div className="relative bg-white/20 backdrop-blur-lg text-white font-bold py-4 px-10 rounded-xl transition-all duration-300 hover:scale-105 flex items-center justify-center transform hover:-translate-y-1 border-2 border-white/30 shadow-2xl">
                  <span className="mr-3 text-xl">🎬</span>
                  Watch Platform Tour
                  <span className="ml-3 text-xl group-hover:translate-x-2 transition-transform">→</span>
                </div>
              </button>
            </div>

            {/* Right Side - Animated Engagement Stats */}
            <div className="relative">
              <div className="bg-gradient-to-br from-yellow-500/20 to-pink-500/20 backdrop-blur-lg rounded-2xl p-6 border-2 border-yellow-400/30 shadow-xl">
                <div className="flex items-center justify-between mb-6">
                  <div className="text-white font-bold text-lg">Live Engagement Stats</div>
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse shadow-md"></div>
                    <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse delay-100 shadow-md"></div>
                    <div className="w-3 h-3 bg-pink-400 rounded-full animate-pulse delay-200 shadow-md"></div>
                  </div>
                </div>
                
                {/* Animated Engagement Bars */}
                <div className="space-y-4">
                  {['Active Group Calls', 'New Messages', 'Voice Room Users', 'Event Participants'].map((item, index) => (
                    <div key={index} className="space-y-2 group cursor-pointer">
                      <div className="flex justify-between items-center">
                        <span className="text-white font-semibold text-sm group-hover:text-yellow-300 transition-colors duration-300">
                          {item}
                        </span>
                        <span className="text-yellow-300 font-bold">
                          {['1,234', '45,678', '8,901', '2,345'][index]}
                          <span className="text-white/70 text-xs ml-1">live</span>
                        </span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-3 shadow-inner">
                        <div 
                          className="bg-gradient-to-r from-yellow-400 to-pink-500 h-3 rounded-full transition-all duration-1000 ease-out shadow-md"
                          style={{ 
                            width: ['78.7%', '89.2%', '66.8%', '55.5%'][index],
                            animation: `growWidth 2s ease-out ${index * 0.3}s`
                          }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-xs text-white/60">
                        <span>0</span>
                        <span>Max</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-yellow-500/30 rounded-full blur-xl animate-bounce shadow-2xl"></div>
              <div className="absolute -bottom-4 -left-4 w-28 h-28 bg-pink-500/30 rounded-full blur-xl animate-bounce delay-1000 shadow-2xl"></div>
            </div>
          </div>
        </div>

        {/* Platform Stats Footer */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
          {[
            { 
              number: '500K+', 
              label: 'Active Users', 
              icon: '👥',
              description: 'Worldwide community' 
            },
            { 
              number: '50K+', 
              label: 'Active Groups', 
              icon: '💬',
              description: 'Communities created' 
            },
            { 
              number: '10K+', 
              label: 'Daily Calls', 
              icon: '📹',
              description: 'Video conferences' 
            },
            { 
              number: '24/7', 
              label: 'Live Support', 
              icon: '⚡',
              description: 'Always available' 
            }
          ].map((stat, index) => (
            <div 
              key={index}
              className="text-center p-8 bg-white/10 backdrop-blur-xl rounded-2xl border-2 border-white/20 hover:bg-white/15 transition-all duration-300 hover:scale-105 transform hover:-translate-y-2 group shadow-xl"
            >
              <div className="text-4xl mb-4 transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
                {stat.icon}
              </div>
              <div className="text-4xl font-black bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">
                {stat.number}
              </div>
              <div className="text-white font-bold text-lg mt-2">
                {stat.label}
              </div>
              <div className="text-white/60 text-sm mt-1">
                {stat.description}
              </div>
              <div className="mt-4 h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
          ))}
        </div>

        {/* Platform Comparison Banner */}
        <div className="mt-16 bg-gradient-to-r from-yellow-500/20 via-pink-500/20 to-orange-500/20 backdrop-blur-lg rounded-2xl p-8 border-2 border-white/25 shadow-2xl">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-white mb-4">
              Why <span className="bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">MASTIVIBES</span> Stands Out
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  title: 'Customization',
                  features: ['Unlimited custom fields', 'Personal profiles', 'Group customization'],
                  icon: '🎨'
                },
                {
                  title: 'Connectivity',
                  features: ['100+ video call participants', 'Group calls', 'Voice rooms'],
                  icon: '📡'
                },
                {
                  title: 'Community',
                  features: ['Public & private groups', 'Event hosting', 'Shared spaces'],
                  icon: '🏆'
                }
              ].map((section, index) => (
                <div key={index} className="text-center p-4">
                  <div className="text-4xl mb-3 animate-bounce">
                    {section.icon}
                  </div>
                  <h4 className="text-xl font-bold text-white mb-3">{section.title}</h4>
                  <ul className="space-y-2">
                    {section.features.map((feature, fIndex) => (
                      <li key={fIndex} className="text-white/80 text-sm">
                        ✓ {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes growWidth {
          from { width: 0%; }
          to { width: var(--target-width); }
        }
        @keyframes float-slow {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg); 
          }
          33% { 
            transform: translateY(-20px) rotate(5deg) scale(1.1); 
          }
          66% { 
            transform: translateY(10px) rotate(-3deg) scale(0.95); 
          }
        }
        @keyframes gradient-flow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
        }
        .animate-gradient-flow {
          background-size: 200% auto;
          animation: gradient-flow 3s ease infinite;
        }
      `}</style>
    </div>
  );
};

export default KeyFeatures;